import { defineStore, acceptHMRUpdate } from 'pinia';

import { getCoachingStat, incTimeLb, incTimeTotal } from '~/lib/coaching-stats';

const getDefaultCoachingStatsTotal = (): CoachingStatSchema => {
  return {
    appId: '',
    historyDate: '',
    historyDateUTC: '',
    id: '',
    isTotal: true,
    local: 0,
    member: '',
    minutes: 0,
    minutesPerWeek: 0,
    objectiveValue: '',
    total: 1,
    totalInMinutes: 0,
    totalInMinutesThisWeek: 0,
  };
};
const getDefaultCoachingStatsLb = (): CoachingStatSchema => {
  return {
    appId: '',
    averageGrade: 0,
    dailyMinutesForActive: 0,
    daysPerWeek: 0,
    globalScore: 0,
    historyDate: '',
    historyDateUTC: '',
    id: '',
    isTotal: true,
    member: '',
    minutes: 0,
    minutesPerWeek: 0,
    objectiveValue: '',
    local: 0,
    total: 0,
    totalInMinutes: 0,
  };
};

export const useCoachingStatsStore = defineStore('CoachingStatsStore', () => {
  const mIncTimes = ref<Map<string, number>>();
  mIncTimes.value = new Map();
  const context = useContextStore();
  const profile = useProfileStore();
  const config = useConfigStore();
  const { mainContent } = useContents();
  const { $db } = useNuxtApp();
  const coachingStatsTotal = ref<CoachingStatSchema>(
    getDefaultCoachingStatsTotal()
  );

  const coachingStatsLb = ref<CoachingStatSchema>(getDefaultCoachingStatsLb());

  const totalInMinutes = computed(() => {
    return coachingStatsTotal.value.totalInMinutes;
  });

  const objectiveMinutesPerWeek = computed(() => {
    return profile?.coachingPlan?.minutesPerWeek || 60;
  });

  const totalInMinutesThisWeek = computed(() => {
    return coachingStatsTotal.value.totalInMinutesThisWeek;
  });

  const averageGradeOn20 = computed(() => {
    const averageGrade = coachingStatsLb.value?.averageGrade || 0;
    return Math.round(averageGrade / 5);
  });

  const incTime = (libraryBookId: string, durationInSeconds: number) => {
    let incTimeValue = mIncTimes.value.has(libraryBookId)
      ? mIncTimes.value.get(libraryBookId)
      : 0;
    incTimeValue += durationInSeconds;
    if (incTimeValue >= 60) {
      mIncTimes.value.set(libraryBookId, 0);
      incTimeLb($db, context.memberId, profile.coachingPlan, libraryBookId, 1);
      incTimeTotal(
        $db,
        context.memberId,
        profile.coachingPlan,
        config.appId,
        1
      );
    } else {
      mIncTimes.value.set(libraryBookId, incTimeValue);
    }
  };

  const refreshValues = async () => {
    coachingStatsTotal.value = await getCoachingStat(
      $db,
      context.memberId,
      profile.coachingPlan,
      config.appId,
      true
    );
    coachingStatsLb.value = await getCoachingStat(
      $db,
      context.memberId,
      profile.coachingPlan,
      mainContent.value.id,
      false
    );
  };

  return {
    incTime,
    mIncTimes,
    totalInMinutes,
    averageGradeOn20,
    refreshValues,
    objectiveMinutesPerWeek,
    totalInMinutesThisWeek,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCoachingStatsStore, import.meta.hot)
  );
}
